/**
 * 3_AsdDDOtherConditions.js — Decisions for Other Conditions (step 3 of the DD)
 *
 * Presents one card per emerging differential for an MDT decision. The condition
 * list is the de-duplicated UNION of the differentials selected at the SSI review
 * and the Observation review (getCombinedEmergingDifferentials), sorted
 * alphabetically so condition N here maps to the same asd_dd_secondaryCondition_N
 * paragraph the backend generates (it iterates the conditions in sorted order).
 *
 * For each condition the MDT chooses "Provide next steps" or "Rule out" and adds
 * optional notes. If there are no differentials at all, this step is skipped
 * entirely and the clinician is redirected to the Final Formulation.
 *
 * Persistence: data.otherConditions.finalComorbidDiagnoses on
 * assessmentStages/diagnosticDecision. We always write the full map (keyed by the
 * current union) so removing a condition upstream prunes its stale decision here.
 *
 * On leaving the page we flush and trigger generation of the per-condition
 * "Secondary Diagnoses" paragraphs.
 */

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import ModularSideNavbar from '../../../components/autismModular/ModularSideNavbar';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import { AutoResizeTextarea } from '../../../components/AutoResizeTextarea';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useModularSectionExitRegeneration } from '../../../hooks/useModularSectionExitRegeneration';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import {
  getAutismDDStepRoutes,
  getCombinedEmergingDifferentials,
  buildAutismDDNavItems,
  getAutismDDRouteForSectionIndex,
  computeAutismDDProgress,
  isOtherConditionsComplete,
} from '../../../utils/autismDDRouting';

// The MDT either provides onward next steps for a condition or rules it out.
// Matches the TOC ADHD "other conditions" decision set so the report voice and
// the backend processor branches are identical.
const DD_DECISIONS = ['Provide next steps', 'Rule out'];

const getPlaceholderText = (decision) => {
  switch (decision) {
    case 'Provide next steps':
      return 'Specify recommended next steps for this condition (e.g. onward referral, further assessment).';
    case 'Rule out':
      return 'Specify the rationale and any recommended next steps, if applicable.';
    default:
      return 'Provide additional notes.';
  }
};

export default function AsdDDOtherConditions() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();

  const {
    header,
    assessmentStages,
    reportContent,
    updateAssessmentStage,
    flushStageWrites,
  } = useContext(ModularPatientDataContext);

  const isPreviewMode = useIsPreviewMode();
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);
  const canEdit = useCanEditStage(header || {}, 'DD', { previewMode: isPreviewMode });

  const ddStage = assessmentStages?.diagnosticDecision;

  // Alphabetically-sorted union → deterministic mapping to backend paragraph keys.
  const conditions = useMemo(
    () => [...getCombinedEmergingDifferentials(assessmentStages)].sort(),
    [assessmentStages],
  );
  // When skipAutismDifferentialsDecisions is true (TOC), this page is not part
  // of the workflow — redirect to Formulation. Also redirect when there are
  // simply no emerging differentials to decide on.
  const hasOther = !wf.skipAutismDifferentialsDecisions && conditions.length > 0;

  // Redirect to Formulation when there is nothing to decide.
  useEffect(() => {
    if (assessmentStages && !hasOther) {
      navigate(getAutismDDStepRoutes(patientId).finalFormulation, { replace: true });
    }
  }, [assessmentStages, hasOther, navigate, patientId]);

  // Lazy initial state from saved decisions, keyed by the current union.
  const [decisions, setDecisions] = useState(() => {
    const stored = ddStage?.data?.otherConditions?.finalComorbidDiagnoses || {};
    const seed = {};
    conditions.forEach((c) => { seed[c] = stored[c] || { decision: null, notes: '' }; });
    return seed;
  });

  // If the union changes (e.g. data arrives after first paint), re-seed only
  // for newly-appearing conditions, preserving any in-progress edits.
  const seededKeyRef = useRef(conditions.join('|'));
  useEffect(() => {
    const key = conditions.join('|');
    if (key === seededKeyRef.current && Object.keys(decisions).length > 0) return;
    seededKeyRef.current = key;
    const stored = ddStage?.data?.otherConditions?.finalComorbidDiagnoses || {};
    setDecisions((prev) => {
      const next = {};
      conditions.forEach((c) => { next[c] = prev[c] || stored[c] || { decision: null, notes: '' }; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditions]);

  // ── Status promotion ────────────────────────────────────────────────────────
  const hasStartedRef = useRef(false);
  const maybePromoteStatus = (updates) => {
    if (!hasStartedRef.current && ddStage?.status === 'notStarted') {
      hasStartedRef.current = true;
      return { ...updates, status: 'inProgress' };
    }
    return updates;
  };

  // ── Dirty tracking ───────────────────────────────────────────────────────────
  const [hasChanges, setHasChanges] = useState(false);

  // ── Single-writer persistence effect ────────────────────────────────────────
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!hydratedRef.current) { hydratedRef.current = true; return; }
    if (!canEdit) return;

    const nextData = {
      ...(ddStage?.data || {}),
      otherConditions: { finalComorbidDiagnoses: decisions },
    };
    let progress = computeAutismDDProgress({ data: nextData, union: conditions, status: ddStage?.status });
    if (ddStage?.status === 'completed' && progress < 1) progress = 1;

    updateAssessmentStage('diagnosticDecision', maybePromoteStatus({
      'data.otherConditions.finalComorbidDiagnoses': decisions,
      progress,
    }));
    setHasChanges(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, decisions]);

  const handleSetDecision = (condition, decision) => {
    if (!canEdit) return;
    setDecisions(prev => ({
      ...prev,
      [condition]: { ...(prev[condition] || { notes: '' }), decision },
    }));
  };

  const handleSetNotes = (condition, notes) => {
    if (!canEdit) return;
    setDecisions(prev => ({
      ...prev,
      [condition]: { ...(prev[condition] || { decision: null }), notes },
    }));
  };

  // Every union condition must have a decision before the clinician can move on
  // to the Final Formulation — mirroring the ADHD "other conditions" gate. This
  // prevents undecided conditions from producing "a determination was not made"
  // paragraphs (or being silently dropped from the report).
  const allDecided = useMemo(
    () => isOtherConditionsComplete({ finalComorbidDiagnoses: decisions }, conditions),
    [decisions, conditions],
  );

  // ── Exit-regeneration via shared hook ────────────────────────────────────────
  // Replaces the hand-rolled unmount net. shouldRegenerate is false when there
  // are no conditions (the page redirects in that case, and there is nothing to
  // generate). Managed exits still call TriggerGenerateDocumentSection directly
  // with a flush promise for faster data readiness.
  const { markRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: 'diagnosticDecision',
    sectionTitle: 'Secondary Diagnoses',
    hasChanges,
    canEdit,
    reportContent,
    shouldRegenerate: hasOther,
  });

  const flushAndGenerate = async () => {
    markRegenHandled();
    const dataReady = flushStageWrites('diagnosticDecision');
    if (canEdit) {
      TriggerGenerateDocumentSection(
        patientId, 'Autism Diagnostic Decision', 'Secondary Diagnoses', [], dataReady,
      );
    }
    await dataReady;
  };

  const { items: navItems, orderedIds } = useMemo(
    () => buildAutismDDNavItems({ assessmentStages, currentSectionIndex: 2, skipDifferentialsDecisions: wf.skipAutismDifferentialsDecisions }),
    [assessmentStages, wf.skipAutismDifferentialsDecisions],
  );

  const navigateToSection = async (index) => {
    await flushAndGenerate();
    navigate(getAutismDDRouteForSectionIndex(patientId, index, hasOther));
  };

  // Block forward progress until every condition is decided (editors only;
  // preview/read-only users can still navigate freely).
  const nextBlocked = canEdit && !allDecided;

  const handleNext = async () => {
    if (nextBlocked) return;
    await flushAndGenerate();
    navigate(getAutismDDStepRoutes(patientId).finalFormulation);
  };

  const handleBack = async () => {
    await flushAndGenerate();
    navigate(getAutismDDStepRoutes(patientId).autismDecision);
  };

  if (!hasOther) return null;

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Diagnostic Decision', getAutismDDStepRoutes(patientId).mdtReview],
    ['Decisions for Other Conditions'],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* Wizard shell — shared sidebar + scrollable content + fixed footer. */}
      <div className="flex h-[calc(100vh-8rem)]">
        <ModularSideNavbar
          items={navItems}
          activeId="otherConditions"
          onSelect={(id) => navigateToSection(orderedIds.indexOf(id))}
        />

        <div className="flex-1 flex flex-col min-h-0">
          {/* Preview mode info banner — shown when opened via a Preview button */}
          {isPreviewMode && <PreviewModeBanner />}
          <div className="flex-1 overflow-y-auto p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Decisions for Other Conditions</h1>
            <p className="text-gray-500 text-sm mt-1">
              The combined emerging differentials from both appointments.
            </p>
            {nextBlocked && (
              <p className="text-xs text-red-600 mt-2">Decide every condition to continue.</p>
            )}
          </div>

          <div className="max-w-3xl mx-auto bg-gray-50 rounded-2xl border border-gray-200 p-6 text-left">
            {conditions.map((condition) => {
              const data = decisions[condition] || { decision: null, notes: '' };
              return (
                <div key={condition} className="mb-6 pb-6 border-b border-gray-200 last:border-0 last:pb-0 last:mb-0">
                  <h4 className="font-semibold text-gray-800 mb-3">{condition}</h4>
                  <ul className="space-y-2">
                    {DD_DECISIONS.map((decision) => {
                      const isActive = data.decision === decision;
                      return (
                        <li
                          key={decision}
                          onClick={() => handleSetDecision(condition, decision)}
                          className={`flex flex-col gap-3 p-2 rounded-lg transition-all duration-300 ${
                            !canEdit ? 'cursor-not-allowed opacity-75' : 'cursor-pointer group'
                          } ${
                            isActive
                              ? 'bg-violet-300 outline outline-2 outline-violet-500'
                              : canEdit ? 'bg-violet-100 hover:bg-violet-200' : 'bg-violet-100'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              isActive ? 'border-violet-700 bg-violet-700' : 'border-gray-300 group-hover:border-violet-400'
                            }`}>
                              {isActive && <div className="w-2 h-2 bg-white rounded" />}
                            </div>
                            <span className={`font-semibold ${isActive ? 'text-violet-900' : 'text-gray-700'}`}>
                              {decision}
                            </span>
                          </div>

                          {isActive && (
                            <AutoResizeTextarea
                              placeholder={getPlaceholderText(decision)}
                              className={`font-medium w-full mt-1 p-2 border rounded-md text-black min-h-[60px] resize-none overflow-hidden ${
                                canEdit ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'
                              }`}
                              value={data.notes || ''}
                              onChange={(e) => handleSetNotes(condition, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              disabled={!canEdit}
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
          </div>

          <ModularStepFooter
            showBack
            onBack={handleBack}
            counterText={`3 of ${navItems.length}`}
            onNext={handleNext}
            nextDisabled={nextBlocked}
            nextLabel="Next: Final Formulation"
          />
        </div>
      </div>
    </Layout>
  );
}
